<script setup lang="ts">
import { ref } from 'vue';
import { uniqueId } from 'lodash-es';
import PlusOutlineIcon from '@/assets/icons/shared/plus-outline.svg';
import ContactCard from '@/modules/counterparty/counterparty-master/components/ContactCard.vue';
import ContactFormModal from '@/modules/counterparty/counterparty-master/components/ContactFormModal.vue';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import type {
  CounterpartyContactFormData,
  CounterpartyContactItem,
} from '@/modules/counterparty/counterparty-master/types/contact-form.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { useWizardOptionalStep } from '@/providers/wizard.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCardList from '@/modules/ui/components/ui/UiCardList.vue';

useWizardOptionalStep();

const { formData, submittedItemIds } = useCounterpartyMaster();

const isFormOpen = ref(false);
const editItem = ref<CounterpartyContactItem | null>(null);

function openForm(item: CounterpartyContactItem | null = null) {
  editItem.value = item;
  isFormOpen.value = true;
}

function onSubmit(data: CounterpartyContactFormData) {
  if (editItem.value) {
    const editingId = editItem.value.id;

    submittedItemIds.delete(editingId);
    formData.value = {
      ...formData.value,
      contacts: formData.value.contacts.map((contact) =>
        contact.id === editingId
          ? {
              ...contact,
              type: data.type,
              contact: data.contact,
              comment: data.comment,
              fio: data.fio,
              position: data.position,
              birthDay: data.birthDay,
              mask: data.mask,
            }
          : contact
      ),
    };
    isFormOpen.value = false;
    return;
  }

  formData.value = {
    ...formData.value,
    contacts: [
      ...formData.value.contacts,
      {
        id: uniqueId(IdPrefix.Control),
        type: data.type,
        contact: data.contact,
        comment: data.comment,
        fio: data.fio,
        position: data.position,
        birthDay: data.birthDay,
        mask: data.mask,
      },
    ],
  };
  isFormOpen.value = false;
}

function removeContact(id: string) {
  submittedItemIds.delete(id);
  formData.value = {
    ...formData.value,
    contacts: formData.value.contacts.filter((contact) => contact.id !== id),
  };
}
</script>

<template>
  <div class="step-contact">
    <div
      class="list"
      :class="{ _empty: !formData.contacts.length }"
    >
      <UiCardList
        v-if="formData.contacts.length"
        class="cards"
        :list="formData.contacts"
        with-add
        equal-height
        @add="openForm()"
      >
        <template #card="{ item }">
          <ContactCard
            :item="item"
            @edit="openForm(item)"
            @remove="removeContact(String(item.id))"
          />
        </template>
      </UiCardList>

      <UiButton
        v-else
        color="black"
        mode="outline"
        @click="openForm()"
      >
        <PlusOutlineIcon v-prefix />
        <span>{{ $t('counterparty.master.steps.contact.add') }}</span>
      </UiButton>
    </div>

    <ContactFormModal
      v-model="isFormOpen"
      :edit-item="editItem"
      :position-options="formData.contacts"
      @submit="onSubmit"
    />
  </div>
</template>

<style scoped lang="scss">
.step-contact {
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.list {
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  border: var(--border-dashed-gray-400);
  border-radius: 8px;

  &._empty {
    justify-content: center;
    align-items: center;
  }
}

.cards {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
}

.list .cards {
  grid-template-columns: repeat(auto-fill, minmax(min(140px, 100%), 1fr));
}
</style>
